import { useState, useRef, useEffect, useCallback } from "react"
import { Plus, ChevronUp, Paperclip, Link2 } from "lucide-react"
import { cn } from "@/lib/utils"

const PLACEHOLDER_PHRASES = [
  "A product launch video for my new app...",
  "A cinematic intro for my YouTube channel...",
  "A short ad for social media with bold text overlays...",
  "A montage of city life set to upbeat music...",
]

const TYPING_SPEED = 40
const DELETING_SPEED = 25
const PAUSE_AFTER_TYPE = 2000
const PAUSE_AFTER_DELETE = 500

interface HomepageChatboxProps {
  onSubmit: (message: string) => void
}

export function HomepageChatbox({ onSubmit }: HomepageChatboxProps) {
  const [message, setMessage] = useState("")
  const [menuOpen, setMenuOpen] = useState(false)
  const [placeholder, setPlaceholder] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const phraseIndex = useRef(0)
  const charIndex = useRef(0)
  const isDeleting = useRef(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const tick = useCallback(() => {
    const currentPhrase = PLACEHOLDER_PHRASES[phraseIndex.current]

    if (!isDeleting.current) {
      charIndex.current++
      setPlaceholder(currentPhrase.slice(0, charIndex.current))

      if (charIndex.current === currentPhrase.length) {
        isDeleting.current = true
        timeoutRef.current = setTimeout(tick, PAUSE_AFTER_TYPE)
      } else {
        timeoutRef.current = setTimeout(tick, TYPING_SPEED)
      }
    } else {
      charIndex.current--
      setPlaceholder(currentPhrase.slice(0, charIndex.current))

      if (charIndex.current === 0) {
        isDeleting.current = false
        phraseIndex.current = (phraseIndex.current + 1) % PLACEHOLDER_PHRASES.length
        timeoutRef.current = setTimeout(tick, PAUSE_AFTER_DELETE)
      } else {
        timeoutRef.current = setTimeout(tick, DELETING_SPEED)
      }
    }
  }, [])

  useEffect(() => {
    timeoutRef.current = setTimeout(tick, PAUSE_AFTER_DELETE)
    return () => clearTimeout(timeoutRef.current)
  }, [tick])

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "auto"
      textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`
    }
  }, [message])

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener("mousedown", handleClick)
      return () => document.removeEventListener("mousedown", handleClick)
    }
  }, [menuOpen])

  const handleSubmit = () => {
    onSubmit(message)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div className="relative w-full max-w-[680px] mx-auto">
      <div
        className={cn(
          "relative bg-white rounded-[20px] shadow-[0_4px_24px_rgba(0,0,0,0.06),0_1px_4px_rgba(0,0,0,0.04)]",
          "border border-black/[0.06]"
        )}
      >
        {/* Textarea area */}
        <div className="px-5 pt-4 pb-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={message ? "" : placeholder}
            rows={2}
            className={cn(
              "w-full resize-none bg-transparent text-[15px] text-gray-900 placeholder:text-gray-500",
              "focus:outline-none leading-relaxed"
            )}
          />
        </div>

        {/* Bottom bar */}
        <div className="flex items-center justify-between px-3 pb-3">
          {/* Plus button */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className={cn(
                "flex items-center justify-center w-9 h-9 rounded-full",
                "border border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300",
                "transition-colors cursor-pointer"
              )}
            >
              <Plus className="w-[18px] h-[18px]" strokeWidth={1.5} />
            </button>

            {/* Dropdown menu */}
            {menuOpen && (
              <div className="absolute left-0 top-full mt-1.5 bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.12)] border border-black/[0.06] py-1.5 min-w-[220px] z-10">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onSubmit(message) }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <Paperclip className="w-[18px] h-[18px] text-gray-500" strokeWidth={1.5} />
                  Add photos or video
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onSubmit(message) }}
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-[14px] text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <Link2 className="w-[18px] h-[18px] text-gray-500" strokeWidth={1.5} />
                  Add URL
                </button>
              </div>
            )}
          </div>

          {/* Submit button */}
          <button
            type="button"
            onClick={handleSubmit}
            className={cn(
              "flex items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors",
              message.trim()
                ? "bg-sky-500 text-white hover:bg-sky-400"
                : "bg-gray-200 text-gray-400 hover:bg-gray-300"
            )}
          >
            <ChevronUp className="w-[18px] h-[18px]" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  )
}
